import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@vaiu/ui/dialog';
import { buildMmMoneyIssuerTitle, type MmMoneyIssuerView } from '@vaiu/utils/mmMoney';

export interface MerchantPublicSummary {
  displayName?: string;
  /** Business / legal name when API returns it separately from displayName. */
  name?: string;
  username?: string;
  avatarUrl?: string;
  bio?: string;
}

export interface MerchantProfileSummaryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issuerUserId: string;
  /** When provided, shown while loading or if fetch fails. */
  fallback?: Pick<MmMoneyIssuerView, 'displayName' | 'name' | 'username' | 'currencyIconUrl'>;
  fetchSummary?: (issuerUserId: string) => Promise<MerchantPublicSummary | null>;
}

/**
 * Read-only merchant snippet for MM$ issuer — platform implements `fetchSummary` (e.g. GET /public/merchants/:id).
 */
export function MerchantProfileSummary({
  open,
  onOpenChange,
  issuerUserId,
  fallback,
  fetchSummary,
}: MerchantProfileSummaryProps) {
  const [data, setData] = useState<MerchantPublicSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !issuerUserId) return;
    if (!fetchSummary) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchSummary(issuerUserId)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, issuerUserId, fetchSummary]);

  const title = buildMmMoneyIssuerTitle({
    issuerUserId,
    displayName: data?.displayName ?? fallback?.displayName,
    name: data?.name ?? fallback?.name,
    username: data?.username ?? fallback?.username,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">Merchant profile summary</DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && (
          <div className="space-y-2 text-sm">
            {(data?.avatarUrl || fallback?.currencyIconUrl) && (
              <img
                src={data?.avatarUrl || fallback?.currencyIconUrl}
                alt=""
                className="h-16 w-16 rounded-md object-cover border"
              />
            )}
            {data?.username && (
              <p className="text-muted-foreground">
                {data.username.startsWith('@') ? data.username : `@${data.username}`}
              </p>
            )}
            {data?.bio && <p>{data.bio}</p>}
            {!fetchSummary && (
              <p className="text-muted-foreground">Public profile API not configured for this app build.</p>
            )}
            {fetchSummary &&
              !loading &&
              !data?.displayName &&
              !data?.name &&
              !fallback?.displayName &&
              !fallback?.name &&
              !data?.username &&
              !data?.bio && (
              <p className="text-muted-foreground font-mono text-xs break-all">Issuer id: {issuerUserId}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
